import { useCallback, useState } from "react";

import { styled } from "@linaria/react";

import Accordion from "@gx/dls/accordion/Accordion";
import GalaxyAvatar from "@gx/dls/avatar/GalaxyAvatar";
import Button, { ButtonSize } from "@gx/dls/buttons/Button";
import FlexItem from "@gx/dls/containers/FlexItem";
import FlexWrapper, {
  AlignItems,
  FlexDirection,
  JustifyContent,
} from "@gx/dls/containers/FlexWrapper";
import HorizontalDivider from "@gx/dls/dividers/HorizontalDivider";
import { InputLabel } from "@gx/dls/inputs/Input";
import NumberInput from "@gx/dls/inputs/NumberInput";
import PasswordInput from "@gx/dls/inputs/PasswordInput";
import RadioInput from "@gx/dls/inputs/RadioInput";
import TextAreaInput, { TextAreaVariant } from "@gx/dls/inputs/TextAreaInput";
import TextInput from "@gx/dls/inputs/TextInput";
import Text, { TextSize, TextVariant, TextWeight } from "@gx/dls/text/Text";
import { withTheme, type PropsWithTheme } from "@gx/dls/theme";
import Widget from "@gx/dls/widget/Widget";
import { SourceType } from "@gx/ts-contracts/services/database/models/public_pb";

import CreateSourceModalHeader from "@/pages/sources/components/create/components/CreateSourceModalHeader";
import CreateSourceModalWrapper from "@/pages/sources/components/create/components/CreateSourceModalWrapper";
import CreateSourceNameInput from "@/pages/sources/components/create/components/CreateSourceNameInput";
import { CREATE_SOURCE_WIDGET_INPUT_WIDTH } from "@/pages/sources/components/create/constants";
import { CreateSourceBrowserbaseActionType } from "@/pages/sources/components/create/sources/browserbase/actions";
import { DEFAULT_MAX_URLS_TO_SCRAPE } from "@/pages/sources/components/create/sources/browserbase/constants";
import CreateSourceBrowserbaseProvider from "@/pages/sources/components/create/sources/browserbase/CreateSourceBrowserbaseProvider";
import { useCreateSourceBrowserbase } from "@/pages/sources/components/create/sources/browserbase/hooks";
import { CreateSourceStep } from "@/pages/sources/components/create/types";
import SourceIcon, { SourceIconSize } from "@/pages/sources/components/SourceIcon";

const BodyWrapper = withTheme(styled.div<PropsWithTheme>`
  flex: 1;
  min-height: 0;
  width: 100%;
  overflow-y: auto;
  background-color: ${({ theme }) => theme.color.background.base};
  padding: 16px;
`);

const FooterWrapper = withTheme(styled.div<PropsWithTheme>`
  padding: 16px;
  background-color: ${({ theme }) => theme.color.background.primary};
`);

interface CreateSourceBrowserbaseProps {
  onClose: () => void;
  onBack: () => void;
}

const CreateSourceBrowserbaseContent = ({ onClose, onBack }: CreateSourceBrowserbaseProps) => {
  const { state, dispatch, nameError, urlError, apiKeyError, projectIdError, isValid, save } =
    useCreateSourceBrowserbase();
  const [isConfigOpen, setIsConfigOpen] = useState(false);

  const handleSourceNameChange = useCallback(
    (name: string) => {
      dispatch({ type: CreateSourceBrowserbaseActionType.SET_SOURCE_NAME, payload: name });
    },
    [dispatch],
  );

  const handleUrlChange = useCallback(
    (value: string) => {
      dispatch({ type: CreateSourceBrowserbaseActionType.SET_URL, payload: value });
    },
    [dispatch],
  );

  const handlePromptChange = useCallback(
    (value: string) => {
      dispatch({ type: CreateSourceBrowserbaseActionType.SET_PROMPT, payload: value });
    },
    [dispatch],
  );

  const handleManagementModeChange = useCallback(
    (mode: boolean) => {
      dispatch({ type: CreateSourceBrowserbaseActionType.SET_MANAGEMENT_MODE, payload: mode });
    },
    [dispatch],
  );

  const handleApiKeyChange = useCallback(
    (value: string) => {
      dispatch({ type: CreateSourceBrowserbaseActionType.SET_API_KEY, payload: value });
    },
    [dispatch],
  );

  const handleProjectIdChange = useCallback(
    (value: string) => {
      dispatch({ type: CreateSourceBrowserbaseActionType.SET_PROJECT_ID, payload: value });
    },
    [dispatch],
  );

  const handleMaxUrlsChange = useCallback(
    (value: number) => {
      dispatch({ type: CreateSourceBrowserbaseActionType.SET_MAX_URLS_TO_SCRAPE, payload: value });
    },
    [dispatch],
  );

  const handleBack = useCallback(() => {
    onBack();
  }, [onBack]);

  const handleSave = useCallback(() => {
    void save();
    onClose();
  }, [save, onClose]);

  return (
    <CreateSourceModalWrapper step={CreateSourceStep.CONFIGURE}>
      <CreateSourceModalHeader
        sourceType={SourceType.BROWSERBASE}
        onClose={onClose}
        onBack={handleBack}
      />
      <HorizontalDivider />

      <BodyWrapper>
        <FlexWrapper direction={FlexDirection.COLUMN} gap={16} fillWidth>
          {/* Source Name */}
          <CreateSourceNameInput
            value={state.sourceName}
            onChange={handleSourceNameChange}
            error={nameError}
          />

          {/* URL Input */}
          <TextInput
            label="URL"
            labelTooltip="Enter the starting URL to scrape. The crawler will follow links from this page."
            value={state.configuration.rootUrl}
            onChange={handleUrlChange}
            placeholder="https://example.com"
            error={urlError ?? undefined}
            isRequired
            fillWidth
          />

          {/* Prompt TextArea */}
          <TextAreaInput
            label="Prompt"
            labelTooltip="Tell us what you're looking for on this website. This helps guide the scraping process."
            value={state.configuration.prompt}
            onChange={handlePromptChange}
            placeholder="Enter any additional instructions..."
            variant={TextAreaVariant.PRIMARY}
            lines={6}
            fillWidth
          />

          {/* Credential Mode Selection */}
          <FlexWrapper direction={FlexDirection.COLUMN} gap={8} fillWidth>
            <Text variant={TextVariant.SECONDARY}>Credentials</Text>
            <FlexWrapper gap={12} fillWidth>
              {/* Galaxy Managed option */}
              <Widget
                fillWidth
                isSelected={state.configuration.isGalaxyManagedCredentials}
                onClick={() => handleManagementModeChange(true)}
              >
                <FlexWrapper alignItems={AlignItems.CENTER} gap={12}>
                  <FlexItem shrink={0}>
                    <RadioInput
                      isSelected={state.configuration.isGalaxyManagedCredentials}
                      onChange={() => handleManagementModeChange(true)}
                    />
                  </FlexItem>
                  <FlexItem shrink={0}>
                    <GalaxyAvatar size={28} />
                  </FlexItem>
                  <FlexWrapper direction={FlexDirection.COLUMN} gap={2}>
                    <FlexWrapper alignItems={AlignItems.CENTER} gap={8}>
                      <Text weight={TextWeight.MEDIUM}>Galaxy managed</Text>
                      <Text variant={TextVariant.TEAL} size={TextSize.BODY_SM}>
                        Recommended
                      </Text>
                    </FlexWrapper>
                    <Text variant={TextVariant.SECONDARY}>
                      We handle API credentials and manage your Browserbase access automatically
                    </Text>
                  </FlexWrapper>
                </FlexWrapper>
              </Widget>

              {/* BYOK option */}
              <Widget
                fillWidth
                isSelected={state.configuration.isGalaxyManagedCredentials === false}
                onClick={() => handleManagementModeChange(false)}
              >
                <FlexWrapper alignItems={AlignItems.CENTER} gap={12}>
                  <FlexItem shrink={0}>
                    <RadioInput
                      isSelected={state.configuration.isGalaxyManagedCredentials === false}
                      onChange={() => handleManagementModeChange(false)}
                    />
                  </FlexItem>
                  <FlexItem shrink={0}>
                    <SourceIcon sourceType={SourceType.BROWSERBASE} size={SourceIconSize.MEDIUM} />
                  </FlexItem>
                  <FlexWrapper direction={FlexDirection.COLUMN} gap={2}>
                    <Text weight={TextWeight.MEDIUM}>Bring your own API key</Text>
                    <Text variant={TextVariant.SECONDARY}>
                      Connect using your own Browserbase account and API credentials
                    </Text>
                  </FlexWrapper>
                </FlexWrapper>
              </Widget>
            </FlexWrapper>
          </FlexWrapper>

          {/* API Key Widget - only shown for BYOK */}
          {state.configuration.isGalaxyManagedCredentials === false && (
            <Widget fillWidth noHover>
              <FlexWrapper direction={FlexDirection.COLUMN} gap={12} fillWidth>
                <FlexWrapper
                  alignItems={AlignItems.CENTER}
                  justifyContent={JustifyContent.SPACE_BETWEEN}
                  fillWidth
                >
                  <InputLabel
                    label="Browserbase API Key"
                    labelTooltip="Your Browserbase API key for authentication."
                    isRequired
                  />
                  <PasswordInput
                    value={state.configuration.apiKey}
                    onChange={handleApiKeyChange}
                    placeholder="Enter API key..."
                    error={apiKeyError ?? undefined}
                    width={CREATE_SOURCE_WIDGET_INPUT_WIDTH}
                  />
                </FlexWrapper>
                <FlexWrapper
                  alignItems={AlignItems.CENTER}
                  justifyContent={JustifyContent.SPACE_BETWEEN}
                  fillWidth
                >
                  <InputLabel
                    label="Browserbase Project ID"
                    labelTooltip="Your Browserbase project ID."
                    isRequired
                  />
                  <TextInput
                    value={state.configuration.projectId}
                    onChange={handleProjectIdChange}
                    placeholder="Enter project ID..."
                    error={projectIdError ?? undefined}
                    width={CREATE_SOURCE_WIDGET_INPUT_WIDTH}
                  />
                </FlexWrapper>
              </FlexWrapper>
            </Widget>
          )}

          {/* Advanced Configuration Accordion */}
          <Accordion
            header="Advanced configuration"
            isOpen={isConfigOpen}
            onToggle={() => setIsConfigOpen(!isConfigOpen)}
          >
            {/* Max URLs to scrape */}
            <FlexWrapper
              alignItems={AlignItems.CENTER}
              justifyContent={JustifyContent.SPACE_BETWEEN}
              fillWidth
            >
              <InputLabel
                label="Max URLs to scrape"
                labelTooltip="The maximum number of URLs to scrape from the starting page."
              />
              <NumberInput
                value={state.configuration.scrapeLimit}
                onChange={handleMaxUrlsChange}
                placeholder={String(DEFAULT_MAX_URLS_TO_SCRAPE)}
                min={1}
                max={500}
                width={CREATE_SOURCE_WIDGET_INPUT_WIDTH}
              />
            </FlexWrapper>
          </Accordion>
        </FlexWrapper>
      </BodyWrapper>

      <HorizontalDivider />
      <FooterWrapper>
        <Button
          label="Save"
          size={ButtonSize.LARGE}
          onClick={handleSave}
          isDisabled={!isValid}
          fillWidth
        />
      </FooterWrapper>
    </CreateSourceModalWrapper>
  );
};

const CreateSourceBrowserbase = (props: CreateSourceBrowserbaseProps) => (
  <CreateSourceBrowserbaseProvider>
    <CreateSourceBrowserbaseContent {...props} />
  </CreateSourceBrowserbaseProvider>
);

export default CreateSourceBrowserbase;
